import { useState, useMemo } from 'react'
import { DataGrid, type DataGridColumn } from '@/components/ui/DataGrid'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useLeads } from '@/hooks/useLeads'
import { ForecastSection } from "@/components/dashboard/ForecastSection"
import { SalesGoalsSection } from "@/components/dashboard/SalesGoalsSection"
import { FunnelVelocitySection } from '@/components/dashboard/FunnelVelocitySection'
import { AgingReportSection } from '@/components/dashboard/AgingReportSection'
import { LossReasonsSection } from '@/components/dashboard/LossReasonsSection'
import { useOpportunities, useCreateOpportunity } from '@/hooks/useOpportunities'
import { useProposals } from '@/hooks/useProposals'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { formatCurrency, formatNumber, formatDate } from '@/lib/utils'
import type { Lead, Opportunity, OpportunityCreate } from '@/types'
import {
  Users, DollarSign, Target, Percent,
  Plus, X, Loader2, Clock,
  BarChart3, PieChart as PieIcon, Lightbulb, FileText
} from 'lucide-react'

// ─── Filter dimensions ──────────────────────────────────────────────────────
type FilterDim = 'owner' | 'status' | 'segment' | 'origin' | 'oppStage'
type Filters = Partial<Record<FilterDim, string>>

const DIM_LABEL: Record<FilterDim, string> = {
  owner: 'Responsável',
  status: 'Status',
  segment: 'Segmento',
  origin: 'Origem',
  oppStage: 'Estágio',
}

// ─── Chart datum with embedded toggle key ───────────────────────────────────
type ChartDatum = { label: string; value: number; color: string; key?: string }

// ─── Simple Bar Chart (horizontal) ──────────────────────────────────────────
function HBarChart({ data, activeKey, onSelect }: { data: ChartDatum[]; activeKey?: string; onSelect?: (key: string) => void }) {
  const max = Math.max(...data.map(d => d.value), 1)
  const hasActive = !!activeKey
  return (
    <div className="space-y-2">
      {data.map((d, i) => {
        const k = d.key ?? d.label
        const isActive = activeKey === k
        const dim = hasActive && !isActive
        const clickable = !!onSelect
        return (
          <div
            key={i}
            className={`flex items-center gap-3 transition-opacity ${dim ? 'opacity-40' : ''} ${clickable ? 'cursor-pointer hover:opacity-100' : ''}`}
            onClick={() => clickable && onSelect!(k)}
          >
            <span className="text-xs text-muted-foreground w-28 truncate text-right">{d.label}</span>
            <div className={`flex-1 bg-muted rounded-full h-5 relative overflow-hidden ${isActive ? 'ring-2 ring-offset-1 ring-primary' : ''}`}>
              <div className={`h-5 rounded-full ${d.color} transition-all`} style={{ width: `${(d.value / max) * 100}%` }} />
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold mix-blend-difference text-white">{d.value}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Vertical Bar Chart (SVG) ───────────────────────────────────────────────
function VBarChart({ data, activeKey, onSelect }: { data: ChartDatum[]; activeKey?: string; onSelect?: (key: string) => void }) {
  const max = Math.max(...data.map(d => d.value), 1)
  const barWidth = Math.min(48, Math.floor(300 / data.length))
  const gap = Math.min(12, Math.floor(60 / data.length))
  const chartW = data.length * (barWidth + gap) - gap
  const chartH = 180
  const padTop = 20
  const padBot = 60
  const hasActive = !!activeKey
  const clickable = !!onSelect

  return (
    <div className="flex justify-center overflow-x-auto">
      <svg width={chartW + 20} height={chartH + padTop + padBot} className="text-foreground">
        {data.map((d, i) => {
          const barH = (d.value / max) * chartH
          const x = 10 + i * (barWidth + gap)
          const y = padTop + chartH - barH
          const k = d.key ?? d.label
          const isActive = activeKey === k
          const opacity = hasActive && !isActive ? 0.35 : 1
          return (
            <g key={i}
               style={{ cursor: clickable ? 'pointer' : 'default', opacity }}
               onClick={() => clickable && onSelect!(k)}>
              <rect x={x} y={y} width={barWidth} height={barH} rx={4} fill={d.color} className="transition-all"
                stroke={isActive ? 'currentColor' : 'none'} strokeWidth={isActive ? 2 : 0} />
              <text x={x + barWidth / 2} y={y - 5} textAnchor="middle" className="fill-foreground text-[11px] font-semibold">{d.value}</text>
              <text x={x + barWidth / 2} y={padTop + chartH + 14} textAnchor="middle" className="fill-muted-foreground text-[10px]">
                {d.label.length > 10 ? d.label.slice(0, 10) + '…' : d.label}
              </text>
              {clickable && (
                <rect x={x} y={padTop} width={barWidth} height={chartH} fill="transparent" />
              )}
            </g>
          )
        })}
        <line x1="10" y1={padTop + chartH} x2={chartW + 10} y2={padTop + chartH} stroke="currentColor" strokeOpacity={0.15} />
      </svg>
    </div>
  )
}

// ─── Simple Donut Chart (SVG) ───────────────────────────────────────────────
function DonutChart({ data, size = 200, activeKey, onSelect }: { data: ChartDatum[]; size?: number; activeKey?: string; onSelect?: (key: string) => void }) {
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total === 0) return <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">Sem dados</div>
  const strokeW = 30
  const padding = strokeW / 2 + 4
  const r = (size - padding * 2) / 2
  const cx = size / 2
  const cy = size / 2
  const circumference = 2 * Math.PI * r
  let offset = 0
  const hasActive = !!activeKey
  const clickable = !!onSelect

  return (
    <div className="flex flex-col items-center gap-3">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {data.filter(d => d.value > 0).map((d, i) => {
          const pct = d.value / total
          const dash = circumference * pct
          const gap = circumference - dash
          const rotation = offset * 360 - 90
          offset += pct
          const k = d.key ?? d.label
          const isActive = activeKey === k
          const opacity = hasActive && !isActive ? 0.3 : 1
          return (
            <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={d.color}
              strokeWidth={isActive ? strokeW + 6 : strokeW}
              strokeDasharray={`${dash} ${gap}`}
              transform={`rotate(${rotation} ${cx} ${cy})`}
              className="transition-all"
              style={{ opacity, cursor: clickable ? 'pointer' : 'default' }}
              onClick={() => clickable && onSelect!(k)} />
          )
        })}
        <text x={cx} y={cy - 6} textAnchor="middle" className="fill-foreground text-2xl font-bold">{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" className="fill-muted-foreground text-[11px]">total</text>
      </svg>
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
        {data.map((d, i) => {
          const k = d.key ?? d.label
          const isActive = activeKey === k
          const dim = hasActive && !isActive
          return (
            <span key={i}
              className={`flex items-center gap-1 text-xs transition-opacity ${dim ? 'opacity-40' : ''} ${clickable ? 'cursor-pointer hover:opacity-100' : ''} ${isActive ? 'font-bold' : ''}`}
              onClick={() => clickable && onSelect!(k)}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
              {d.label}: {d.value}
            </span>
          )
        })}
      </div>
    </div>
  )
}

// ─── Stat Card ──────────────────────────────────────────────────────────────
function StatCard({ title, value, icon: Icon, color, sub }: { title: string; value: string; icon: typeof Users; color: string; sub?: string }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${color}`}><Icon className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground truncate">{title}</p>
          <p className="text-lg font-bold truncate">{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground truncate">{sub}</p>}
        </div>
      </div>
    </Card>
  )
}

// ─── New Opportunity Modal ──────────────────────────────────────────────────
function NewOpportunityModal({ onClose, leads }: { onClose: () => void; leads: Lead[] }) {
  const navigate = useNavigate()
  const createOpp = useCreateOpportunity()
  const [form, setForm] = useState<OpportunityCreate>({ lead_id: '', name: '', value: 0, probability: 50 })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.lead_id || !form.name) return
    createOpp.mutate(form, {
      onSuccess: (opp) => { onClose(); navigate(`/opportunities/${opp.id}`) },
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background rounded-xl shadow-xl max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Nova Oportunidade</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Lead / Empresa *</label>
            <select className="w-full mt-1 px-3 py-2 rounded-md border bg-background text-sm" required
              value={form.lead_id} onChange={e => setForm(f => ({ ...f, lead_id: e.target.value }))}>
              <option value="">Selecione um lead...</option>
              {leads.map(l => <option key={l.id} value={l.id}>{l.company?.name || l.company?.trading_name || l.id}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Nome da Oportunidade *</label>
            <Input className="mt-1" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Implementação IAM Empresa X" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Valor Estimado (R$)</label>
              <Input className="mt-1" type="number" min={0} step={100} value={form.value || ''} onChange={e => setForm(f => ({ ...f, value: Number(e.target.value) }))} />
            </div>
            <div>
              <label className="text-sm font-medium">Probabilidade (%)</label>
              <Input className="mt-1" type="number" min={0} max={100} value={form.probability || ''} onChange={e => setForm(f => ({ ...f, probability: Number(e.target.value) }))} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Data Prevista de Fechamento</label>
            <Input className="mt-1" type="date" value={form.expected_close_date || ''} onChange={e => setForm(f => ({ ...f, expected_close_date: e.target.value || null }))} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={createOpp.isPending || !form.lead_id || !form.name}>
              {createOpp.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Criar
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Constants for labels/colors ────────────────────────────────────────────
const STATUS_LABELS: Record<string, string> = { suspect: 'Suspect', prospect: 'Prospect', mql: 'MQL', sql: 'SQL', opportunity: 'Oportunidade', disqualified: 'Desqualificado', customer: 'Cliente' }
const STATUS_COLORS: Record<string, string> = { suspect: '#94a3b8', prospect: '#60a5fa', mql: '#a78bfa', sql: '#f59e0b', opportunity: '#10b981', disqualified: '#ef4444', customer: '#22c55e' }
const STAGE_LABELS: Record<string, string> = { qualified: 'Qualificado', proposal_sent: 'Proposta Enviada', negotiation: 'Negociação', closing: 'Fechamento', won: 'Ganho', lost: 'Perdido' }
const STAGE_COLORS: Record<string, string> = { qualified: '#3b82f6', proposal_sent: '#f59e0b', negotiation: '#f97316', closing: '#ef4444', won: '#22c55e', lost: '#6b7280' }
const SEGMENT_COLORS: Record<string, string> = {
  'Sucroenergético': '#16a34a', 'Agro': '#65a30d', 'Logística': '#0284c7',
  'Tecnologia': '#7c3aed', 'AI/ML': '#db2777', 'GeoTech': '#ea580c',
  'TI': '#6366f1', 'Cooperativa': '#0891b2', 'Consultoria': '#d97706',
  'Comunidade': '#64748b', 'AgTech': '#059669',
}
const ORIGIN_LABELS: Record<string, string> = {
  'active_prospecting': 'Prospecção Ativa',
  'bdr_partners': 'BDR/Parceiros',
  'event': 'Evento',
  'website': 'Website',
}
const ORIGIN_COLORS: Record<string, string> = {
  'active_prospecting': '#7c3aed',
  'bdr_partners': '#16a34a',
  'event': '#ea580c',
  'website': '#0284c7',
}
const OWNER_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#6366f1']

const SEGMENT_NONE = '__none__'

// Human-readable label for an active filter chip
function filterChipLabel(dim: FilterDim, key: string): string {
  if (dim === 'status') return STATUS_LABELS[key] || key
  if (dim === 'oppStage') return STAGE_LABELS[key] || key
  if (dim === 'origin') return ORIGIN_LABELS[key] || key
  if (dim === 'segment') return key === SEGMENT_NONE ? 'Sem segmento' : key
  return key
}

// ─── Dashboard ──────────────────────────────────────────────────────────────
export function Dashboard() {
  const navigate = useNavigate()

  const { data: leadsData } = useLeads({ perPage: 200 })
  const { data: oppsData } = useOpportunities({ perPage: 200 })
  const { data: proposalsData } = useProposals({ perPage: 200 })

  const leads = leadsData?.items || []
  const opportunities = oppsData?.items || []
  const proposals = proposalsData?.items || []

  // Tab state — persiste na URL via ?tab=estrategicos|taticos
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab: 'estrategicos' | 'taticos' =
    (searchParams.get('tab') as 'estrategicos' | 'taticos') || 'estrategicos'
  function setActiveTab(tab: 'estrategicos' | 'taticos') {
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }

  // ─── Filter state with toggle/replace/add semantics ─────────────────────
  const [filters, setFilters] = useState<Filters>({})

  function toggle(dim: FilterDim, key: string) {
    setFilters(prev => {
      const next = { ...prev }
      if (prev[dim] === key) delete next[dim]
      else next[dim] = key
      return next
    })
  }

  function clearOne(dim: FilterDim) {
    setFilters(prev => { const n = { ...prev }; delete n[dim]; return n })
  }

  function clearAll() { setFilters({}) }

  // ─── Apply filters to leads / opportunities ───────────────────────────────
  const filteredLeads = useMemo(() => {
    return leads.filter(l => {
      if (filters.owner && (l.owner_name || 'Sem responsável') !== filters.owner) return false
      if (filters.status && l.status !== filters.status) return false
      if (filters.origin && l.origin !== filters.origin) return false
      if (filters.segment) {
        const seg = l.company?.segment || SEGMENT_NONE
        if (seg !== filters.segment) return false
      }
      return true
    })
  }, [leads, filters])

  const filteredOpps = useMemo(() => {
    return opportunities.filter(o => {
      if (filters.owner && (o.owner_name || 'Sem responsável') !== filters.owner) return false
      if (filters.oppStage && o.stage !== filters.oppStage) return false
      // Cross-link: when a lead-side filter is active, restrict opps to their lead set
      if (filters.status || filters.origin || filters.segment) {
        const leadIds = new Set(filteredLeads.map(l => l.id))
        if (!leadIds.has(o.lead_id)) return false
      }
      return true
    })
  }, [opportunities, filters, filteredLeads])

  // ─── KPIs (use filtered data) ─────────────────────────────────────────────
  const totalLeads = filteredLeads.length
  const pipelineValue = filteredOpps.filter(o => !['won', 'lost'].includes(o.stage)).reduce((s, o) => s + o.value, 0)
  const wonValue = filteredOpps.filter(o => o.stage === 'won').reduce((s, o) => s + o.value, 0)
  const wonCount = filteredOpps.filter(o => o.stage === 'won').length
  const lostCount = filteredOpps.filter(o => o.stage === 'lost').length
  const winRate = (wonCount + lostCount) > 0 ? Math.round((wonCount / (wonCount + lostCount)) * 100) : 0
  const totalOppsCount = filteredOpps.filter(o => o.stage !== 'lost').length
  const pendingProposals = proposals.filter(p => p.status === 'pending_approval').length

  // ─── Aggregations ────────────────────────────────────────────────────────
  // Owner palette is computed from the unfiltered leads so colors remain stable
  const ownerColorByName = useMemo(() => {
    const map = new Map<string, number>()
    leads.forEach(l => { const n = l.owner_name || 'Sem responsável'; map.set(n, (map.get(n) || 0) + 1) })
    const ordered = Array.from(map.entries()).sort((a, b) => b[1] - a[1]).map(([n]) => n)
    const colors: Record<string, string> = {}
    ordered.forEach((n, i) => { colors[n] = OWNER_COLORS[i % OWNER_COLORS.length] })
    return colors
  }, [leads])

  const leadsByOwner = useMemo<ChartDatum[]>(() => {
    const map = new Map<string, number>()
    filteredLeads.forEach(l => { const n = l.owner_name || 'Sem responsável'; map.set(n, (map.get(n) || 0) + 1) })
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([label, value]) => ({ label, value, key: label, color: 'bg-purple-500' }))
  }, [filteredLeads])

  const leadsByStatus = useMemo<ChartDatum[]>(() => {
    const map = new Map<string, number>()
    filteredLeads.forEach(l => map.set(l.status, (map.get(l.status) || 0) + 1))
    return Array.from(map.entries()).map(([status, value]) => ({
      key: status,
      label: STATUS_LABELS[status] || status,
      value,
      color: STATUS_COLORS[status] || '#6b7280',
    }))
  }, [filteredLeads])

  const leadsBySegment = useMemo<ChartDatum[]>(() => {
    const map = new Map<string, number>()
    filteredLeads.forEach(l => {
      const seg = l.company?.segment || SEGMENT_NONE
      map.set(seg, (map.get(seg) || 0) + 1)
    })
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])
      .map(([seg, value]) => ({
        key: seg,
        label: seg === SEGMENT_NONE ? 'Sem segmento' : seg,
        value,
        color: SEGMENT_COLORS[seg] || '#6b7280',
      }))
  }, [filteredLeads])

  const leadsByOrigin = useMemo<ChartDatum[]>(() => {
    const map = new Map<string, number>()
    filteredLeads.forEach(l => map.set(l.origin, (map.get(l.origin) || 0) + 1))
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])
      .map(([key, value]) => ({
        key,
        label: ORIGIN_LABELS[key] || key,
        value,
        color: ORIGIN_COLORS[key] || '#6b7280',
      }))
  }, [filteredLeads])

  const oppsByOwner = useMemo<ChartDatum[]>(() => {
    const map = new Map<string, number>()
    filteredOpps.forEach(o => { const n = o.owner_name || 'Sem responsável'; map.set(n, (map.get(n) || 0) + 1) })
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([label, value]) => ({ label, value, key: label, color: 'bg-purple-500' }))
  }, [filteredOpps])

  const oppsByStage = useMemo<ChartDatum[]>(() => {
    const map = new Map<string, number>()
    filteredOpps.forEach(o => map.set(o.stage, (map.get(o.stage) || 0) + 1))
    return Array.from(map.entries()).map(([stage, value]) => ({
      key: stage,
      label: STAGE_LABELS[stage] || stage,
      value,
      color: STAGE_COLORS[stage] || '#6b7280',
    }))
  }, [filteredOpps])

  const oppsList = useMemo(() => {
    return [...filteredOpps].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )
  }, [filteredOpps])

  const leadsList = useMemo(() => {
    return [...filteredLeads].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )
  }, [filteredLeads])

  // ─── Render ──────────────────────────────────────────────────────────────
  const activeFilterEntries = Object.entries(filters) as [FilterDim, string][]

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b">
        <div className="flex items-center justify-between h-16 px-6">
          <div>
            <h1 className="text-xl font-semibold">Dashboard</h1>
            <p className="text-sm text-muted-foreground">Visão geral do pipeline de vendas</p>
          </div>
        </div>
        {/* Tabs — Dashboards Estratégicos vs Táticos */}
        <div className="flex gap-1 px-6 pb-1 border-t border-border/50">
          <button
            onClick={() => setActiveTab('estrategicos')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'estrategicos'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Dashboards Estratégicos
          </button>
          <button
            onClick={() => setActiveTab('taticos')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'taticos'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Dashboards Táticos
          </button>
        </div>
      </header>

      <div className="p-6 space-y-6">
        {/* Filter chips (só aparece no Táticos — Estratégicos não usam filters cross-dashboard) */}
        {activeTab === 'taticos' && activeFilterEntries.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-muted/30">
            <span className="text-xs font-medium text-muted-foreground mr-1">Filtros ativos:</span>
            {activeFilterEntries.map(([dim, key]) => (
              <button
                key={dim}
                onClick={() => clearOne(dim)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                title="Clique para remover este filtro"
              >
                <span className="text-muted-foreground">{DIM_LABEL[dim]}:</span>
                <span>{filterChipLabel(dim, key)}</span>
                <X className="h-3 w-3" />
              </button>
            ))}
            <button
              onClick={clearAll}
              className="ml-auto text-xs text-muted-foreground hover:text-foreground underline"
            >
              Limpar todos
            </button>
          </div>
        )}

        {activeTab === 'estrategicos' && (
          <>
            <ForecastSection />
            <SalesGoalsSection />
            <FunnelVelocitySection />
            <LossReasonsSection />
          </>
        )}

        {activeTab === 'taticos' && (
          <>
        {/* KPI Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <StatCard title="Total Leads" value={formatNumber(totalLeads)} icon={Users} color="bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400" />
          <StatCard title="Pipeline" value={formatCurrency(pipelineValue)} icon={DollarSign} color="bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400" />
          <StatCard title="Deals Ganhos" value={formatCurrency(wonValue)} icon={Target} color="bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400" sub={`${wonCount} oportunidades`} />
          <StatCard title="Win Rate" value={`${winRate}%`} icon={Percent} color="bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-400" />
          <StatCard title="Oportunidades" value={formatNumber(totalOppsCount)} icon={Lightbulb} color="bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-400" />
          <StatCard title="Propostas Pendentes" value={formatNumber(pendingProposals)} icon={FileText} color="bg-pink-100 text-pink-600 dark:bg-pink-900 dark:text-pink-400" />
        </div>

        {/* Charts Row 1 — Leads */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" /> Leads por Responsável</CardTitle></CardHeader>
            <CardContent>
              {leadsByOwner.length > 0
                ? <HBarChart data={leadsByOwner} activeKey={filters.owner} onSelect={(k) => toggle('owner', k)} />
                : <p className="text-sm text-muted-foreground text-center py-8">Nenhum lead encontrado</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><PieIcon className="h-5 w-5" /> Leads por Origem</CardTitle></CardHeader>
            <CardContent className="flex justify-center">
              <DonutChart data={leadsByOrigin} activeKey={filters.origin} onSelect={(k) => toggle('origin', k)} />
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 3 — Oportunidades */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" /> Oportunidades por Responsável</CardTitle></CardHeader>
            <CardContent>
              {oppsByOwner.length > 0
                ? <HBarChart data={oppsByOwner} activeKey={filters.owner} onSelect={(k) => toggle('owner', k)} />
                : <p className="text-sm text-muted-foreground text-center py-8">Nenhuma oportunidade encontrada</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><PieIcon className="h-5 w-5" /> Oportunidades por Estágio</CardTitle></CardHeader>
            <CardContent className="flex justify-center">
              <DonutChart data={oppsByStage} activeKey={filters.oppStage} onSelect={(k) => toggle('oppStage', k)} />
            </CardContent>
          </Card>
        </div>

        {/* Aging Report (movido pra cá em 2026-05-15 — fica acima da Lista de Oportunidades.
            Recebe ownerFilter do estado global de filtros pra reagir aos clicks em outros dashboards
            de "Responsável" (HBarCharts). Click no owner_name dentro da tabela alterna o filtro. */}
        <AgingReportSection
          ownerFilter={filters.owner}
          onOwnerToggle={(name) => toggle('owner', name)}
        />

        {/* Lista de Oportunidades */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-indigo-500" />
              Lista de Oportunidades
              {oppsList.length > 0 && <span className="ml-2 text-xs font-normal bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 px-2 py-0.5 rounded-full">{oppsList.length}</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {oppsList.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhuma oportunidade encontrada</p>
            ) : (
              <DataGrid
                gridId="dashboard-opps-list"
                columns={[
                  { id: 'name', header: 'Oportunidade', accessor: (o: Opportunity) => <span className="font-medium">{o.name}</span>, sortValue: (o) => o.name, filterValue: (o) => o.name },
                  { id: 'company', header: 'Empresa', accessor: (o: Opportunity) => <span className="text-muted-foreground">{o.lead_company_name || '—'}</span>, sortValue: (o) => o.lead_company_name || '', filterValue: (o) => o.lead_company_name || '' },
                  { id: 'owner', header: 'Responsável', accessor: (o: Opportunity) => <span className="text-muted-foreground">{o.owner_name || '—'}</span>, sortValue: (o) => o.owner_name || '', filterValue: (o) => o.owner_name || '' },
                  { id: 'stage', header: 'Estágio', accessor: (o: Opportunity) => { const c = STAGE_COLORS[o.stage] || '#6b7280'; return <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold bg-muted"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />{STAGE_LABELS[o.stage] || o.stage}</span> }, sortValue: (o) => o.stage, filterValue: (o) => STAGE_LABELS[o.stage] || o.stage },
                  { id: 'value', header: 'Valor', accessor: (o: Opportunity) => <span className="font-medium">{formatCurrency(o.value)}</span>, sortValue: (o) => o.value, filterValue: (o) => formatCurrency(o.value), align: 'right' as const },
                  { id: 'updated', header: 'Último Update', accessor: (o: Opportunity) => { const d = Math.floor((Date.now() - new Date(o.updated_at).getTime()) / 86400000); return <span className="text-xs text-muted-foreground"><Clock className="h-3 w-3 inline mr-1" />{d === 0 ? 'hoje' : `${d}d atrás`}</span> }, sortValue: (o) => new Date(o.updated_at).getTime(), filterValue: (o) => formatDate(o.updated_at), align: 'right' as const },
                ] as DataGridColumn<Opportunity>[]}
                data={oppsList}
                onRowClick={(o) => navigate(`/opportunities/${o.id}`)}
                pageSize={15}
                emptyMessage="Nenhuma oportunidade encontrada"
              />
            )}
          </CardContent>
        </Card>

        {/* Lista de Leads */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              Lista de Leads
              {leadsList.length > 0 && <span className="ml-2 text-xs font-normal bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 py-0.5 rounded-full">{leadsList.length}</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leadsList.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhum lead encontrado</p>
            ) : (
              <DataGrid
                gridId="dashboard-leads-list"
                columns={[
                  { id: 'company', header: 'Empresa', accessor: (l: Lead) => <span className="font-medium">{l.company?.name || l.company?.trading_name || '—'}</span>, sortValue: (l) => l.company?.name || l.company?.trading_name || '', filterValue: (l) => l.company?.name || l.company?.trading_name || '' },
                  { id: 'segment', header: 'Segmento', accessor: (l: Lead) => <span className="text-muted-foreground">{l.company?.segment || '—'}</span>, sortValue: (l) => l.company?.segment || '', filterValue: (l) => l.company?.segment || '' },
                  { id: 'owner', header: 'Responsável', accessor: (l: Lead) => <span className="text-muted-foreground">{l.owner_name || '—'}</span>, sortValue: (l) => l.owner_name || '', filterValue: (l) => l.owner_name || '' },
                  { id: 'status', header: 'Status', accessor: (l: Lead) => { const c = STATUS_COLORS[l.status] || '#6b7280'; return <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold bg-muted"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />{STATUS_LABELS[l.status] || l.status}</span> }, sortValue: (l) => l.status, filterValue: (l) => STATUS_LABELS[l.status] || l.status },
                  { id: 'origin', header: 'Origem', accessor: (l: Lead) => <span className="text-muted-foreground text-xs">{ORIGIN_LABELS[l.origin] || l.origin}</span>, sortValue: (l) => l.origin, filterValue: (l) => ORIGIN_LABELS[l.origin] || l.origin },
                  { id: 'updated', header: 'Último Update', accessor: (l: Lead) => { const d = Math.floor((Date.now() - new Date(l.updated_at).getTime()) / 86400000); return <span className="text-xs text-muted-foreground"><Clock className="h-3 w-3 inline mr-1" />{d === 0 ? 'hoje' : `${d}d atrás`}</span> }, sortValue: (l) => new Date(l.updated_at).getTime(), filterValue: (l) => formatDate(l.updated_at), align: 'right' as const },
                ] as DataGridColumn<Lead>[]}
                data={leadsList}
                onRowClick={() => navigate('/leads')}
                pageSize={15}
                emptyMessage="Nenhum lead encontrado"
              />
            )}
          </CardContent>
        </Card>
          </>
        )}
      </div>
    </div>
  )
}
